import { emptyFilesListText } from 'owa-locstrings/lib/strings/emptyfileslisttext.locstring.json';
import loc, { format } from 'owa-localize';
import {
    DetailsLayout,
    TilesLayout,
    CompactDetailsLayout,
    ItemCacheProvider,
    ItemsScope,
    ItemsView,
    defaultClickActionControl,
    itemsContextMenuControl,
    ControlHandlerProvider,
    IItemCache,
    getItemsScope,
    selectedItemsFacet,
    itemTileControl,
    emptyFolderControl,
    BaseText,
    IItemColumn,
} from '@ms/items-view';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { getParameterStore } from '../store/ParametersStore';
import { getFilterStore } from '../store/FilterStore';
import { itemCacheStore, getItemScopeItemKey } from '../store/cache';
import { getColumns } from '../utils/getColumns';
import { defaultClickActionControlHandler } from '../handlers/defaultClickHandler';
import { selectColumnSortAction } from '../actions/internalActions';
import {
    openInNewTabCommand,
    downloadCommand,
    printCommand,
    copyToClipboardCommand,
    shareByEmailCommand,
} from '../utils/getCommandBarActions';
import { fileKeyFacet } from '../Schema/Facets';
import { AttachmentModel } from 'owa-attachment-full-view';
import { getAttachment } from 'owa-attachment-model-store/lib/lazyIndex';
import { TypeOfAttachment } from 'owa-attachment-model-store';
import { IEmptyFolderStyles } from '@ms/items-view/lib/private/components/itemsEmptyFolder/EmptyFolder.styles';
import { Link } from 'office-ui-fabric-react/lib/Link';
import { SentBySubtitleText } from '../strings.locstring.json';
import { FilesHubFilterPillContainer } from 'owa-files-filter-pill';
import { getFilterPills } from '../utils/getFilterPills';
import { FileExtensionType } from 'owa-files-filter-file-extensions-filter';
import { updateItemsViewCache } from '../store/cache';
import AttachmentItemsSortBy from 'owa-service/lib/contract/AttachmentItemsSortBy';
import { getIsLightBaseTheme } from 'owa-theme';

const styles = require('./FilesViewList.scss');

export const FilesViewList = observer(function FilesViewList(props: {}) {
    const parameterStore = getParameterStore();

    let selectedExtensions: FileExtensionType[] = [];
    getFilterStore().extensionGroupFilter.selectedValue.forEach((value: string, key: string) => {
        selectedExtensions.push(Number(key) as FileExtensionType);
    });

    return (
        <div className={styles.wrapper}>
            <div
                className={
                    getFilterStore().dateRangeFilter.selectedValue.dateRange !== null ||
                    selectedExtensions.length !== 0
                        ? styles.pills
                        : null
                }>
                <FilesHubFilterPillContainer
                    pills={getFilterPills(
                        getFilterStore().dateRangeFilter.selectedValue,
                        selectedExtensions
                    )}
                    onAfterRemove={onAfterRemove}
                />
            </div>
            <ItemCacheProvider
                store={itemCacheStore}
                key={`${parameterStore.mailboxInfo &&
                    parameterStore.mailboxInfo.primarySmtpAddress}-list`}>
                <ItemsScope itemsScopeItemKey={getItemScopeItemKey()}>
                    <ControlHandlerProvider
                        controlHandlers={{
                            ...defaultClickActionControl.pack(defaultClickActionControlHandler),
                            ...itemsContextMenuControl.pack(itemsContextMenuControlHandler),
                            ...itemTileControl.pack(itemTileControlHandler),
                            ...emptyFolderControl.pack(emptyFolderControlHandler),
                        }}>
                        <ItemsView>
                            <DetailsLayout
                                className={styles.detailsList}
                                columns={getColumns(getFilterStore().columnSort.selectedValue)}
                                onColumnHeaderClick={onColumnHeaderClick}
                            />
                            <TilesLayout className={styles.tilesList} />
                            <CompactDetailsLayout isAvailable={false} />
                        </ItemsView>
                    </ControlHandlerProvider>
                </ItemsScope>
            </ItemCacheProvider>
        </div>
    );
});

function onColumnHeaderClick(event: React.MouseEvent<HTMLElement>, column: IItemColumn): void {
    let columnOptions = [];
    if (column.key === 'name') {
        columnOptions.push(AttachmentItemsSortBy.AttachmentName);
    } else if (column.key === 'recievedDate') {
        columnOptions.push(AttachmentItemsSortBy.AttachmentReceivedTime);
    } else if (column.key === 'sentBy') {
        columnOptions.push(AttachmentItemsSortBy.AttachmentFrom);
    } else if (column.key === 'subject') {
        columnOptions.push(AttachmentItemsSortBy.AttachmentSubject);
    }

    if (columnOptions.length !== 0) {
        if (column.isSorted && !column.isSortedDescending) {
            columnOptions.push(AttachmentItemsSortBy.DescendingOrder);
        } else if (column.isSorted && column.isSortedDescending) {
            columnOptions = [0];
        }
        selectColumnSortAction(columnOptions);
    }
}

function onAfterRemove() {
    // Update list data according to new filters
    updateItemsViewCache();
}

function itemsContextMenuControlHandler(
    itemCache: IItemCache,
    props: typeof itemsContextMenuControl.inputType
): typeof itemsContextMenuControl.returnType {
    const { itemsScopeItemKey } = getItemsScope(itemCache);

    const selectedItems = itemCache.demandItemFacet(selectedItemsFacet, itemsScopeItemKey);
    const selectedItemKeys = selectedItems && selectedItems.itemKeys;
    const hasSelection = selectedItemKeys && selectedItemKeys.length > 0;

    const parameterStore = getParameterStore();

    if (hasSelection) {
        for (const itemKey of selectedItemKeys) {
            const { id } = itemCache.demandItemFacet(fileKeyFacet, itemKey);
            const attachmentId = {
                Id: id,
                mailboxInfo: parameterStore.mailboxInfo,
            };
            const attachment: AttachmentModel = getAttachment(attachmentId);
            if (attachment.type === TypeOfAttachment.Link) {
                return {
                    commands: [openInNewTabCommand, copyToClipboardCommand],
                };
            }
        }
    }

    return {
        commands: [downloadCommand, printCommand, copyToClipboardCommand, shareByEmailCommand],
    };
}

function itemTileControlHandler(
    itemCache: IItemCache,
    props: typeof itemTileControl.inputType
): typeof itemTileControl.returnType {
    const { itemKey } = props;

    const { id } = itemCache.demandItemFacet(fileKeyFacet, itemKey);
    const attachmentId = {
        Id: id,
        mailboxInfo: getParameterStore().mailboxInfo,
    };
    const attachment: AttachmentModel = getAttachment(attachmentId);

    return {
        itemName:
            attachment.type === TypeOfAttachment.Link ? (
                <div className={styles.tileName}>
                    <Link data-automationid="name" title={attachment.model.Name || ''}>
                        {attachment.model.Name || ''}
                    </Link>
                </div>
            ) : (
                <BaseText
                    className={styles.tileName}
                    data-automationid="name"
                    text={attachment.model.Name || ''}
                    title={attachment.model.Name || ''}
                />
            ),
        itemActivity: (
            <div>
                {format(
                    loc(SentBySubtitleText),
                    itemCache.demandItemFacet(fileKeyFacet, itemKey).sender
                )}
            </div>
        ),
    };
}

function emptyFolderControlHandler(
    itemCache: IItemCache,
    props: typeof emptyFolderControl.inputType
): typeof emptyFolderControl.returnType {
    return {
        emptyFolderIconUrl: getIsLightBaseTheme()
            ? require('owa-empty-state/lib/svg/colored/emptyAttachments.svg')
            : require('owa-empty-state/lib/svg/gray/emptyAttachments.svg'),
        emptyFolderTitle: loc(emptyFilesListText),
        getStyles: getEmptyFolderOverrideStyle,
    };
}

function getEmptyFolderOverrideStyle(): Partial<IEmptyFolderStyles> {
    return {
        emptyFolderImageContainer: {
            height: '150px',
            width: '150px',
        },
    };
}
